import { useEffect, useState } from "react";
import { useStore, buildWhatsappLink } from "../lib/store";
import { Calendar, Clock, User, Phone, Mail, MessageCircle, CheckCircle2, Smartphone } from "lucide-react";
import { YapeModal } from "./YapeModal";

export const Reservation = ({ preselectedService, onAfterSubmit }) => {
  const { business, services } = useStore();
  const [form, setForm] = useState({
    nombre: "",
    telefono: "",
    email: "",
    servicio: "",
    fecha: "",
    hora: "",
    comentarios: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [yapeOpen, setYapeOpen] = useState(false);
  const [lastReservation, setLastReservation] = useState(null);

  useEffect(() => {
    if (preselectedService) {
      setForm((f) => ({ ...f, servicio: preselectedService.id }));
      const el = document.getElementById("reservas");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [preselectedService]);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = (e) => {
    e.preventDefault();
    const service = services.find((s) => s.id === form.servicio);
    const serviceLabel = service ? service.name : "Por definir";

    const message =
      `Hola AURA Estética & Spa! Quisiera reservar una cita:\n\n` +
      `• Nombre: ${form.nombre}\n` +
      `• Teléfono: ${form.telefono}\n` +
      `• Email: ${form.email}\n` +
      `• Servicio: ${serviceLabel}\n` +
      `• Fecha: ${form.fecha}\n` +
      `• Hora: ${form.hora}\n` +
      (form.comentarios ? `• Comentarios: ${form.comentarios}\n` : "") +
      (business.yape?.enabled ? `\nRealizaré el depósito de S/ ${business.yape.depositAmount || 30} por Yape para confirmar la cita.` : "") +
      `\n\nQuedo atenta a la confirmación. ¡Gracias!`;

    const link = buildWhatsappLink(business.whatsappNumberOnly || business.whatsapp, message);
    window.open(link, "_blank");

    // Persist to localStorage as a "registro" so admin can view
    const list = JSON.parse(localStorage.getItem("aura_reservations") || "[]");
    const record = { ...form, serviceLabel, createdAt: new Date().toISOString() };
    list.push(record);
    localStorage.setItem("aura_reservations", JSON.stringify(list));

    setLastReservation(record);
    setSubmitted(true);
    if (onAfterSubmit) onAfterSubmit();
  };

  return (
    <section id="reservas" className="section-padding relative overflow-hidden" data-testid="reservation-section">
      {/* Decorative background */}
      <div className="absolute inset-0 bg-gradient-to-b from-beige-50 to-white"></div>
      <div className="absolute -left-32 top-20 w-96 h-96 rounded-full bg-rose-100 opacity-40 blur-3xl"></div>
      <div className="absolute -right-32 bottom-20 w-96 h-96 rounded-full bg-gold-50 opacity-50 blur-3xl"></div>

      <div className="container-aura relative grid lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5">
          <div className="text-xs tracking-luxe uppercase text-gold mb-4">Reservas</div>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-ink leading-tight mb-6">
            Agenda tu <span className="italic text-gold">cita</span>
          </h2>
          <div className="gold-divider mb-6"></div>
          <p className="text-base md:text-lg text-ink-soft font-light leading-relaxed mb-8">
            Completa el formulario y te conectaremos directamente por WhatsApp para confirmar tu cita y resolver cualquier duda.
          </p>

          <ul className="space-y-4">
            {[
              "Confirmación rápida por WhatsApp",
              "Atención personalizada y profesional",
              "Productos premium y técnicas avanzadas",
              "Ambiente íntimo y de máxima relajación",
            ].map((t) => (
              <li key={t} className="flex items-start gap-3">
                <CheckCircle2 size={18} className="text-gold mt-0.5 flex-shrink-0" />
                <span className="text-sm text-ink-soft font-light">{t}</span>
              </li>
            ))}
          </ul>

          {/* Yape deposit info card */}
          {business.yape?.enabled && (
            <button
              type="button"
              onClick={() => setYapeOpen(true)}
              data-testid="yape-open-info"
              className="mt-8 w-full text-left p-5 rounded-2xl border border-[#702f8a]/20 bg-gradient-to-br from-white to-[#fbf6ff] hover:border-[#702f8a]/50 hover:shadow-lg transition-all group"
            >
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#702f8a] to-[#4f1f6b] text-white flex items-center justify-center flex-shrink-0">
                  <Smartphone size={20} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] tracking-luxe uppercase text-[#702f8a] font-medium">Reserva con Yape</div>
                  <div className="font-serif text-lg text-ink mt-0.5">Depósito S/ {business.yape.depositAmount || 30} para confirmar</div>
                  <div className="text-xs text-ink-soft mt-1 font-light">
                    Ver QR y datos de pago →
                  </div>
                </div>
              </div>
            </button>
          )}
        </div>

        <div className="lg:col-span-7">
          {submitted ? (
            <div className="bg-white border border-beige-200 rounded-3xl p-8 md:p-10 shadow-[0_20px_50px_-20px_rgba(0,0,0,0.1)]" data-testid="reservation-success">
              <div className="w-16 h-16 rounded-full bg-gold-50 flex items-center justify-center mx-auto mb-5">
                <CheckCircle2 size={32} className="text-gold" />
              </div>
              <h3 className="font-serif text-3xl text-ink text-center mb-3">¡Solicitud enviada!</h3>
              <p className="text-center text-ink-soft font-light mb-6">
                Te abrimos WhatsApp con tu solicitud lista. Para asegurar tu cita, realiza el depósito por Yape.
              </p>

              {business.yape?.enabled && (
                <button
                  onClick={() => setYapeOpen(true)}
                  data-testid="yape-open-success"
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-gradient-to-r from-[#702f8a] to-[#4f1f6b] text-white text-xs tracking-luxe uppercase font-medium hover:brightness-110 transition-all"
                >
                  <Smartphone size={16} />
                  Pagar depósito por Yape — S/ {business.yape.depositAmount || 30}
                </button>
              )}

              <div className="flex justify-center mt-4">
                <button
                  onClick={() => setSubmitted(false)}
                  className="text-xs tracking-luxe uppercase text-ink-soft hover:text-gold transition-colors"
                  data-testid="reservation-new"
                >
                  Realizar otra reserva
                </button>
              </div>
            </div>
          ) : (
            <form
              onSubmit={onSubmit}
              className="bg-white border border-beige-200 rounded-3xl p-6 md:p-10 shadow-[0_20px_50px_-20px_rgba(0,0,0,0.1)]"
              data-testid="reservation-form"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <Field icon={<User size={16} />} label="Nombre completo" required>
                  <input
                    required
                    data-testid="res-input-nombre"
                    value={form.nombre}
                    onChange={onChange("nombre")}
                    type="text"
                    placeholder="Tu nombre"
                    className="reservation-input"
                  />
                </Field>
                <Field icon={<Phone size={16} />} label="Teléfono" required>
                  <input
                    required
                    data-testid="res-input-telefono"
                    value={form.telefono}
                    onChange={onChange("telefono")}
                    type="tel"
                    placeholder="+57 300 000 0000"
                    className="reservation-input"
                  />
                </Field>
                <Field icon={<Mail size={16} />} label="Correo electrónico" required>
                  <input
                    required
                    data-testid="res-input-email"
                    value={form.email}
                    onChange={onChange("email")}
                    type="email"
                    placeholder="tu@email.com"
                    className="reservation-input"
                  />
                </Field>
                <Field label="Servicio" required>
                  <select
                    required
                    data-testid="res-select-servicio"
                    value={form.servicio}
                    onChange={onChange("servicio")}
                    className="reservation-input"
                  >
                    <option value="">Selecciona un servicio</option>
                    {services.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field icon={<Calendar size={16} />} label="Fecha" required>
                  <input
                    required
                    data-testid="res-input-fecha"
                    value={form.fecha}
                    onChange={onChange("fecha")}
                    type="date"
                    className="reservation-input"
                  />
                </Field>
                <Field icon={<Clock size={16} />} label="Hora" required>
                  <input
                    required
                    data-testid="res-input-hora"
                    value={form.hora}
                    onChange={onChange("hora")}
                    type="time"
                    className="reservation-input"
                  />
                </Field>
              </div>

              <div className="mt-5">
                <Field label="Comentarios adicionales">
                  <textarea
                    data-testid="res-input-comentarios"
                    value={form.comentarios}
                    onChange={onChange("comentarios")}
                    rows={4}
                    placeholder="Cuéntanos cualquier detalle importante…"
                    className="reservation-input resize-none"
                  />
                </Field>
              </div>

              <button
                type="submit"
                data-testid="reservation-submit"
                className="mt-7 w-full btn-gold py-4"
              >
                <MessageCircle size={18} />
                Enviar reserva por WhatsApp
              </button>

              <p className="text-xs text-center text-ink-soft mt-4 font-light">
                Al enviar, abriremos WhatsApp con tu solicitud lista para confirmar.
              </p>

              <style>{`
                .reservation-input {
                  width: 100%;
                  padding: 0.75rem 1rem;
                  border-radius: 0.75rem;
                  border: 1px solid #F1E9D9;
                  background-color: #FDFBF7;
                  font-size: 0.95rem;
                  color: #2C2825;
                  outline: none;
                  transition: all 0.25s ease;
                }
                .reservation-input:focus {
                  border-color: #D4AF37;
                  background-color: #fff;
                  box-shadow: 0 0 0 4px rgba(212,175,55,0.1);
                }
              `}</style>
            </form>
          )}
        </div>
      </div>

      <YapeModal open={yapeOpen} onClose={() => setYapeOpen(false)} reservationData={lastReservation} />
    </section>
  );
};

const Field = ({ icon, label, required, children }) => (
  <label className="block">
    <div className="flex items-center gap-1.5 text-xs tracking-luxe uppercase text-ink-soft mb-2">
      {icon}
      <span>
        {label} {required && <span className="text-gold">*</span>}
      </span>
    </div>
    {children}
  </label>
);
